import { isPtacPathwaySettings } from '@alpinehomeair/common';
import { RootState } from 'app/store';
import { ConfigurationType } from 'data/types';
import { selectActivePtacConfiguration } from 'utils/active-ptac-configuration';
import {
  configurationHasBeenFetched,
  isConfigurationComplete,
  isConfigurationEligible,
  isPtacDesignComplete,
  isPtacDesignReady
} from 'utils/configuration-eligibility';
import {
  isDuctedDesignComplete,
  selectDuctedDesignConfigurationIds,
  selectDuctedPairLinkMode,
  selectEligibleDuctedSystemIds
} from 'utils/ducted-design';
import { getExpectedFullFetchConfigIds } from 'utils/valid-systems-settlement';

function isPtacPathway(state: RootState): boolean {
  return isPtacPathwaySettings(state.project.projectSettings);
}

function ptacFetchedConfigurations(state: RootState): ConfigurationType[] {
  const configuration = selectActivePtacConfiguration(state);
  return configuration !== undefined &&
    isPtacDesignComplete(configuration) &&
    state.ptacSelection.systems.status === 'loaded'
    ? [configuration]
    : [];
}

function completeInOrder(
  configurations: ConfigurationType[],
  state: RootState,
  otherIsIncluded: (configuration: ConfigurationType) => boolean
): ConfigurationType[] {
  const designIds = selectDuctedDesignConfigurationIds(state);
  const eligibleSystemIds = selectEligibleDuctedSystemIds(state);
  const linkMode = selectDuctedPairLinkMode(state);
  return configurations.filter((configuration) =>
    designIds.has(configuration.uniqueId)
      ? isDuctedDesignComplete(configuration, eligibleSystemIds, linkMode)
      : otherIsIncluded(configuration)
  );
}

export function selectExpectedFullFetchConfigIds(
  state: RootState,
  leftoverSpaceIds?: ReadonlySet<string>
): string[] {
  if (isPtacPathway(state)) return [];
  const designIds = selectDuctedDesignConfigurationIds(state);
  return getExpectedFullFetchConfigIds(
    state.configurations.configurations.filter(
      (configuration) => !designIds.has(configuration.uniqueId)
    ),
    leftoverSpaceIds
  );
}

export function selectCompleteConfigurations(
  state: RootState,
  leftoverSpaceIds?: ReadonlySet<string>
): ConfigurationType[] {
  const { configurations } = state.configurations;
  if (isPtacPathway(state)) return configurations.filter(isPtacDesignComplete);
  return completeInOrder(configurations, state, (configuration) =>
    isConfigurationComplete(configuration, leftoverSpaceIds)
  );
}

export function selectFetchedConfigurations(
  state: RootState,
  leftoverSpaceIds?: ReadonlySet<string>
): ConfigurationType[] {
  if (isPtacPathway(state)) return ptacFetchedConfigurations(state);
  const { configurations } = state.configurations;
  const { systemCombinationsFull } = state.systems;
  return completeInOrder(
    configurations,
    state,
    (configuration) =>
      isConfigurationComplete(configuration, leftoverSpaceIds) &&
      configurationHasBeenFetched(configuration, systemCombinationsFull)
  );
}

export function selectEligibleConfigurations(
  state: RootState,
  leftoverSpaceIds?: ReadonlySet<string>
): ConfigurationType[] {
  const { configurations } = state.configurations;
  if (isPtacPathway(state)) return configurations.filter(isPtacDesignReady);
  const { systemCombinationsFull } = state.systems;
  return completeInOrder(configurations, state, (configuration) =>
    isConfigurationEligible(
      configuration,
      systemCombinationsFull,
      leftoverSpaceIds
    )
  );
}
